import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Seo from "@/components/Seo";
import { LOCAL_SEO_PAGES, LOCAL_SEO_KEYS } from "@/pages/public/seo/localSeoContent";
import { MapPin, ArrowRight } from "lucide-react";

const BASE = "https://yosimediastudio.com";

/**
 * Bilingual index of the Huntsville-local landing pages. Both languages are
 * hand-authored real content — no auto-translation.
 */
export default function LocalSeoIndex({ lang }) {
  const isEs = lang === "es";
  const canonical = `${BASE}${isEs ? "/es/servicios" : "/services/huntsville"}`;
  const altHref = `${BASE}${isEs ? "/services/huntsville" : "/es/servicios"}`;

  const title = isEs
    ? "Servicios en Huntsville, AL | Yosi Media Studio"
    : "Services in Huntsville, AL | Yosi Media Studio";
  const description = isEs
    ? "Diseño web, SEO, marketing digital, automatización con IA e impresión para negocios de Huntsville, Alabama."
    : "Web design, SEO, digital marketing, AI automation and printing for Huntsville, Alabama businesses.";
  const heading = isEs ? "Servicios en Huntsville, Alabama" : "Services in Huntsville, Alabama";
  const intro = isEs
    ? "Servicios digitales completos para negocios de Huntsville y el Valle del Tennessee. Elija un servicio para ver detalles, precios de referencia y respuestas a las preguntas locales más comunes."
    : "End-to-end digital services for Huntsville and Tennessee Valley businesses. Pick a service to see scope, indicative pricing and answers to the local questions we hear most.";

  return (
    <MarketingLayout>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        lang={lang}
        hreflangAlternates={[
          { hreflang: "en-us", href: `${BASE}/services/huntsville` },
          { hreflang: "es-us", href: `${BASE}/es/servicios` },
          { hreflang: "x-default", href: `${BASE}/services/huntsville` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Yosi Media Studio",
          url: BASE,
          telephone: "+12562888270",
          email: "info@yosimediastudio.com",
          areaServed: { "@type": "State", name: "Alabama" },
          address: {
            "@type": "PostalAddress",
            addressLocality: "Huntsville",
            addressRegion: "AL",
            addressCountry: "US",
          },
        }}
      />

      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16" data-testid="seo-index">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-medium mb-5">
          <MapPin className="w-3 h-3" /> Huntsville, Alabama
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-4">{heading}</h1>
        <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-10">{intro}</p>

        <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {LOCAL_SEO_KEYS.map((key) => {
            const page = LOCAL_SEO_PAGES[key][lang];
            const href = isEs ? `/es/servicios/${page.slug}` : `/services/${page.slug}`;
            return (
              <li key={key}>
                <Link to={href} className="block rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover:border-blue-500/40 transition-all group" data-testid={`seo-index-${key}`}>
                  <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">{page.serviceType}</div>
                  <h2 className="font-display text-2xl font-bold mb-2 group-hover:text-blue-300">{page.h1}</h2>
                  <p className="text-sm text-slate-400 leading-relaxed mb-4">{page.description}</p>
                  <span className="text-sm text-blue-400 inline-flex items-center gap-1">
                    {isEs ? "Ver detalles" : "See details"} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </MarketingLayout>
  );
}
